/**
 * SysAdmin Enhetsköer
 * Lista Jasper SESSION_START-ägarrader med filter och actions.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  QueueListIcon,
  PlayIcon,
  TrashIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import { PageBackButton } from '@/components/common/PageBackButton';
import toast from 'react-hot-toast';
import OrgTreeSelect from '@/components/common/OrgTreeSelect';
import {
  deviceQueueService,
  DeviceQueueCounts,
  DeviceQueueItem,
  DeviceQueueType,
  DeviceQueueView,
  GetverResult,
} from '@/services/sysadmin/deviceQueueService';
import {
  buildDeviceQueueCsv,
  deviceQueueCsvFilename,
  downloadCsv,
} from './deviceQueueCsv';

const DEFAULT_TYPES: DeviceQueueType[] = ['session'];

const statusPillClass = (status: string): string => {
  const s = status.toLowerCase();
  if (s === 'done') return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300';
  if (s === 'failed') return 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300';
  if (s === 'cancelled') return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
  return 'bg-bgElevated text-textDefault border border-border';
};

const formatDateTime = (value: string | null): string => {
  if (!value) return '–';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '–';
  return new Intl.DateTimeFormat('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(d);
};

const DeviceQueueMonitor: React.FC = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState<DeviceQueueItem[]>([]);
  const [counts, setCounts] = useState<DeviceQueueCounts>({ queue: 0, done: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [organizationFilter, setOrganizationFilter] = useState('');
  const [search, setSearch] = useState('');
  const [view, setView] = useState<DeviceQueueView>('queue');
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [fwModal, setFwModal] = useState<GetverResult | null>(null);
  const [markImei, setMarkImei] = useState('');

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const { items: data, counts: nextCounts } = await deviceQueueService.list({
        types: DEFAULT_TYPES,
        organizationId: organizationFilter || undefined,
        view,
      });
      setItems(data);
      setCounts(nextCounts);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || t('sysAdmin.deviceQueues.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [organizationFilter, view, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (item) =>
        item.imei.toLowerCase().includes(q) ||
        (item.registrationNumber || '').toLowerCase().includes(q) ||
        (item.makeModel || '').toLowerCase().includes(q) ||
        (item.groupName || '').toLowerCase().includes(q) ||
        item.purposeOrStatus.toLowerCase().includes(q) ||
        (item.detail || '').toLowerCase().includes(q),
    );
  }, [items, search]);

  const typeLabel = (type: DeviceQueueType): string => {
    switch (type) {
      case 'session':
        return t('sysAdmin.deviceQueues.typeSession');
      case 'command':
        return t('sysAdmin.deviceQueues.typeCommand');
      case 'pnp':
        return t('sysAdmin.deviceQueues.typePnp');
      case 'force_non_obd':
        return t('sysAdmin.deviceQueues.typeForceNonObd');
      default: {
        const _exhaustive: never = type;
        return String(_exhaustive);
      }
    }
  };

  const handleMarkInteresting = async () => {
    const imei = markImei.trim();
    if (!/^\d{13,15}$/.test(imei)) {
      toast.error(t('sysAdmin.deviceQueues.markFail'));
      return;
    }
    setBusyKey(`mark:${imei}`);
    try {
      await deviceQueueService.markInteresting(imei);
      toast.success(t('sysAdmin.deviceQueues.markOk'));
      setMarkImei('');
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || t('sysAdmin.deviceQueues.markFail'));
    } finally {
      setBusyKey(null);
    }
  };

  const handleForce = async (item: DeviceQueueItem) => {
    if (!window.confirm(t('sysAdmin.deviceQueues.confirmForce'))) return;
    const key = `force:${item.queueType}:${item.id}`;
    setBusyKey(key);
    try {
      const res = await deviceQueueService.force(item.queueType, item.id);
      if (res.success) {
        const queued = Boolean(res.data?.queued || res.data?.offline);
        if (queued) {
          toast(res.message || t('sysAdmin.deviceQueues.forceQueued'));
        } else {
          toast.success(res.message || t('sysAdmin.deviceQueues.forceOk'));
        }
      } else {
        toast.error(res.message || t('sysAdmin.deviceQueues.forceFail'));
      }
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || t('sysAdmin.deviceQueues.forceFail'));
    } finally {
      setBusyKey(null);
    }
  };

  const handleRemove = async (item: DeviceQueueItem) => {
    if (!window.confirm(t('sysAdmin.deviceQueues.confirmRemove'))) return;
    const key = `remove:${item.queueType}:${item.id}`;
    setBusyKey(key);
    try {
      const res = await deviceQueueService.remove(item.queueType, item.id);
      toast.success(res.message || t('sysAdmin.deviceQueues.removeOk'));
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || t('sysAdmin.deviceQueues.removeFail'));
    } finally {
      setBusyKey(null);
    }
  };

  const handleGetver = async (item: DeviceQueueItem) => {
    const key = `fw:${item.imei}`;
    setBusyKey(key);
    try {
      const res = await deviceQueueService.getver(item.imei);
      setFwModal(res);
      if (res.delivered) toast.success(t('sysAdmin.deviceQueues.getverOk'));
      else toast(res.message || t('sysAdmin.deviceQueues.getverQueued'));
    } catch (e: any) {
      toast.error(e?.response?.data?.error || t('sysAdmin.deviceQueues.getverFail'));
    } finally {
      setBusyKey(null);
    }
  };

  const handleExportCsv = () => {
    if (filteredItems.length === 0) {
      toast.error(t('sysAdmin.deviceQueues.exportEmpty'));
      return;
    }
    const csv = buildDeviceQueueCsv(
      [
        t('sysAdmin.deviceQueues.colType'),
        t('sysAdmin.deviceQueues.colReg'),
        t('sysAdmin.deviceQueues.colMakeModel'),
        t('sysAdmin.deviceQueues.colGroup'),
        t('sysAdmin.deviceQueues.colImei'),
        t('sysAdmin.deviceQueues.colLastSeen'),
      ],
      filteredItems.map((item) => ({
        phase: typeLabel(item.queueType),
        registrationNumber: item.registrationNumber || '',
        makeModel: item.makeModel || '',
        groupName: item.groupName || '',
        imei: item.imei,
        lastSeen: formatDateTime(item.lastSeen),
      })),
    );
    downloadCsv(deviceQueueCsvFilename(view), csv);
    toast.success(t('sysAdmin.deviceQueues.exportOk', { count: filteredItems.length }));
  };

  return (
    <div className="p-4 bg-bgContainer rounded-lg h-full overflow-auto">
      <div className="flex items-center gap-3 mb-4">
        <PageBackButton />
        <div className="p-2 bg-BgPrimaryLight rounded-lg">
          <QueueListIcon className="w-6 h-6 text-bgPrimary" />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-textDefault">{t('sysAdmin.deviceQueues.title')}</h1>
          <p className="text-sm text-textSecondary">{t('sysAdmin.deviceQueues.description')}</p>
        </div>
        <button
          type="button"
          onClick={handleExportCsv}
          disabled={isLoading || filteredItems.length === 0}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-bgElevated hover:bg-bgContainer text-sm text-textDefault disabled:opacity-40"
          title={t('common.exportCsv')}
        >
          <ArrowDownTrayIcon className="w-4 h-4" />
          {t('common.exportCsv')}
        </button>
        <button
          type="button"
          onClick={() => void load()}
          disabled={isLoading}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-bgElevated hover:bg-bgContainer text-sm text-textDefault"
          title={t('sysAdmin.deviceQueues.countsHint', {
            queue: counts.queue,
            done: counts.done,
          })}
        >
          <ArrowPathIcon className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          {t('sysAdmin.deviceQueues.refresh')}
          <span className="text-xs text-textSecondary tabular-nums">
            {t('sysAdmin.deviceQueues.countsShort', {
              queue: counts.queue,
              done: counts.done,
            })}
          </span>
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div
          className="inline-flex rounded-lg border border-border bg-bgElevated p-0.5"
          role="group"
          aria-label={t('sysAdmin.deviceQueues.viewLabel')}
        >
          <button
            type="button"
            onClick={() => setView('queue')}
            className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
              view === 'queue'
                ? 'bg-bgPrimary text-white'
                : 'text-textDefault hover:bg-bgContainer'
            }`}
          >
            {t('sysAdmin.deviceQueues.viewQueue')}
            <span className="ml-1.5 tabular-nums opacity-90">({counts.queue})</span>
          </button>
          <button
            type="button"
            onClick={() => setView('done')}
            className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
              view === 'done'
                ? 'bg-bgPrimary text-white'
                : 'text-textDefault hover:bg-bgContainer'
            }`}
          >
            {t('sysAdmin.deviceQueues.viewDone')}
            <span className="ml-1.5 tabular-nums opacity-90">({counts.done})</span>
          </button>
        </div>
        <OrgTreeSelect
          value={organizationFilter}
          onChange={setOrganizationFilter}
          showEmptyOption
          emptyLabel={t('fleet.filterAllOrganizations')}
          variant="chip"
          chipLabel={t('fleet.organization')}
          isActive={organizationFilter !== ''}
          className="inline-block w-auto min-w-[180px]"
        />
        {view === 'queue' ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={markImei}
              onChange={(e) => setMarkImei(e.target.value)}
              placeholder="IMEI"
              className="w-40 px-3 py-1.5 rounded-lg border border-border bg-bgElevated text-sm text-textDefault"
            />
            <button
              type="button"
              onClick={() => void handleMarkInteresting()}
              disabled={!!busyKey}
              className="px-3 py-1.5 rounded-lg text-sm border border-border bg-bgElevated hover:bg-bgContainer text-textDefault"
            >
              {t('sysAdmin.deviceQueues.markInteresting')}
            </button>
          </div>
        ) : null}
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('sysAdmin.deviceQueues.searchPlaceholder')}
          className="ml-auto min-w-[200px] px-3 py-1.5 rounded-lg border border-border bg-bgElevated text-sm text-textDefault"
        />
      </div>

      {isLoading ? (
        <p className="text-textSecondary text-sm">{t('common.loading')}</p>
      ) : filteredItems.length === 0 ? (
        <p className="text-textSecondary text-sm">
          {view === 'done'
            ? t('sysAdmin.deviceQueues.emptyDone')
            : t('sysAdmin.deviceQueues.empty')}
        </p>
      ) : (
        <div className="overflow-x-auto border border-border rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-bgElevated text-textSecondary">
              <tr>
                <th className="text-left px-3 py-2 font-medium">{t('sysAdmin.deviceQueues.colType')}</th>
                <th className="text-left px-3 py-2 font-medium">{t('sysAdmin.deviceQueues.colReg')}</th>
                <th className="text-left px-3 py-2 font-medium">{t('sysAdmin.deviceQueues.colMakeModel')}</th>
                <th className="text-left px-3 py-2 font-medium">{t('sysAdmin.deviceQueues.colGroup')}</th>
                <th className="text-left px-3 py-2 font-medium">{t('sysAdmin.deviceQueues.colImei')}</th>
                <th className="text-left px-3 py-2 font-medium">{t('sysAdmin.deviceQueues.colFw')}</th>
                <th className="text-left px-3 py-2 font-medium">{t('sysAdmin.deviceQueues.colLastSeen')}</th>
                <th className="text-left px-3 py-2 font-medium">
                  {view === 'done'
                    ? t('sysAdmin.deviceQueues.colCompleted')
                    : t('sysAdmin.deviceQueues.colAdded')}
                </th>
                <th className="text-left px-3 py-2 font-medium">{t('sysAdmin.deviceQueues.colStatus')}</th>
                <th className="text-right px-3 py-2 font-medium">{t('sysAdmin.deviceQueues.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => {
                const rowKey = `${item.queueType}:${item.id}`;
                const stuck = Boolean(item.detail && item.detail.includes('STUCK'));
                return (
                  <tr
                    key={rowKey}
                    className={`border-t border-border hover:bg-bgElevated/50 ${
                      stuck ? 'bg-amber-50/80 dark:bg-amber-950/20' : ''
                    }`}
                  >
                    <td className="px-3 py-2 whitespace-nowrap">{typeLabel(item.queueType)}</td>
                    <td className="px-3 py-2 whitespace-nowrap font-medium text-textDefault">
                      {item.registrationNumber || '–'}
                    </td>
                    <td className="px-3 py-2 text-textDefault">
                      {item.makeModel || '–'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{item.groupName || '–'}</td>
                    <td className="px-3 py-2 font-mono text-xs">{item.imei}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{item.currentFirmware || '–'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(item.lastSeen)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(item.addedAt)}</td>
                    <td className="px-3 py-2 max-w-xs">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${statusPillClass(
                          item.purposeOrStatus,
                        )}`}
                      >
                        {item.purposeOrStatus}
                      </span>
                      {item.detail ? (
                        <div className="text-xs text-textSecondary truncate mt-0.5" title={item.detail}>
                          {item.detail}
                        </div>
                      ) : null}
                      {item.commandCount != null ? (
                        <div className="text-xs text-textSecondary">
                          {t('sysAdmin.deviceQueues.cmdCount', { count: item.commandCount })}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        {view === 'queue' ? (
                          <button
                            type="button"
                            title={t('sysAdmin.deviceQueues.actionForce')}
                            disabled={busyKey != null}
                            onClick={() => void handleForce(item)}
                            className="p-1.5 rounded hover:bg-bgContainer text-bgPrimary disabled:opacity-40"
                          >
                            <PlayIcon className="w-4 h-4" />
                          </button>
                        ) : null}
                        <button
                          type="button"
                          title={t('sysAdmin.deviceQueues.actionGetver')}
                          disabled={busyKey != null}
                          onClick={() => void handleGetver(item)}
                          className="p-1.5 rounded hover:bg-bgContainer text-textDefault disabled:opacity-40"
                        >
                          <CpuChipIcon className="w-4 h-4" />
                        </button>
                        {view === 'queue' ? (
                          <button
                            type="button"
                            title={t('sysAdmin.deviceQueues.actionRemove')}
                            disabled={busyKey != null}
                            onClick={() => void handleRemove(item)}
                            className="p-1.5 rounded hover:bg-bgContainer text-red-600 disabled:opacity-40"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {fwModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-bgContainer border border-border rounded-lg shadow-lg max-w-lg w-full p-4">
            <h2 className="text-lg font-semibold text-textDefault mb-2">
              {t('sysAdmin.deviceQueues.getverTitle')}
            </h2>
            <p className="text-sm text-textSecondary mb-1">IMEI: {fwModal.imei}</p>
            <p className="text-sm text-textDefault mb-2">
              {t('sysAdmin.deviceQueues.colFw')}: {fwModal.firmware || '–'}
            </p>
            <pre className="text-xs bg-bgElevated border border-border rounded p-2 overflow-auto max-h-48 whitespace-pre-wrap">
              {fwModal.response || fwModal.message}
            </pre>
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                className="px-3 py-1.5 rounded-lg bg-bgPrimary text-white text-sm"
                onClick={() => setFwModal(null)}
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DeviceQueueMonitor;
